import type {
  PackagingBom,
  PackagingBomCreateInput,
  PackagingBomDetail,
  PackagingBomLine,
  PackagingBomStatus,
  ProductPackagingMap,
} from './types'
import type { AssemblyType, IstaLevel } from '@/services/boms/types'

export type PackagingBomsApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class PackagingBomsApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'PackagingBomsApiClientError'
    this.code = code
    this.status = status
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as PackagingBomsApiError
  throw new PackagingBomsApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

export async function listPackagingBomsApi(filter?: {
  product_id?: string
  status?: PackagingBomStatus
  q?: string
}): Promise<PackagingBom[]> {
  const params = new URLSearchParams()
  if (filter?.product_id) params.set('product_id', filter.product_id)
  if (filter?.status) params.set('status', filter.status)
  if (filter?.q) params.set('q', filter.q)
  const qs = params.toString()

  const res = await fetch(`/api/packaging-boms${qs ? `?${qs}` : ''}`)
  const body = await parseJson<
    { ok: true; packagingBoms: PackagingBom[] } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Lấy danh sách Packaging BOM thất bại')
  return (body as { packagingBoms: PackagingBom[] }).packagingBoms
}

export async function getPackagingBomApi(
  id: string,
): Promise<PackagingBomDetail | null> {
  const res = await fetch(`/api/packaging-boms/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; packagingBom: PackagingBomDetail } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Lấy chi tiết Packaging BOM thất bại')
  return (body as { packagingBom: PackagingBomDetail }).packagingBom
}

export async function createPackagingBomApi(
  input: PackagingBomCreateInput,
): Promise<PackagingBom> {
  const res = await fetch('/api/packaging-boms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<
    { ok: true; packagingBom: PackagingBom } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Tạo Packaging BOM thất bại')
  return (body as { packagingBom: PackagingBom }).packagingBom
}

export async function activatePackagingBomApi(
  id: string,
): Promise<PackagingBom> {
  const res = await fetch(
    `/api/packaging-boms/${encodeURIComponent(id)}/activate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    },
  )
  const body = await parseJson<
    { ok: true; packagingBom: PackagingBom } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Kích hoạt Packaging BOM thất bại')
  return (body as { packagingBom: PackagingBom }).packagingBom
}

export async function listPackagingMapsApi(
  productId: string,
): Promise<ProductPackagingMap[]> {
  const res = await fetch(
    `/api/packaging-maps?product_id=${encodeURIComponent(productId)}`,
  )
  const body = await parseJson<
    { ok: true; maps: ProductPackagingMap[] } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Lấy danh sách mapping đóng gói thất bại')
  return (body as { maps: ProductPackagingMap[] }).maps
}

export async function upsertPackagingMapApi(input: {
  product_id: string
  assembly_type: AssemblyType
  ista_level: IstaLevel
  packaging_bom_family_id: string
}): Promise<ProductPackagingMap> {
  const res = await fetch('/api/packaging-maps', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<
    { ok: true; map: ProductPackagingMap } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Lưu mapping đóng gói thất bại')
  return (body as { map: ProductPackagingMap }).map
}

export async function createPackagingBomLineApi(
  bomId: string,
  line: {
    name_raw: string
    spec_raw?: string | null
    material_id?: string | null
    qty_per: number
    uom_hint?: string | null
    notes?: string | null
    sort_order?: number
  },
): Promise<PackagingBomLine> {
  const res = await fetch(
    `/api/packaging-boms/${encodeURIComponent(bomId)}/lines`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(line),
    },
  )
  const body = await parseJson<
    { ok: true; line: PackagingBomLine } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Thêm dòng đóng gói thất bại')
  return (body as { line: PackagingBomLine }).line
}

export async function updatePackagingBomLineApi(
  bomId: string,
  lineId: string,
  patch: Partial<{
    name_raw: string
    spec_raw: string | null
    material_id: string | null
    qty_per: number
    uom_hint: string | null
    notes: string | null
    sort_order: number
  }>,
): Promise<PackagingBomLine> {
  const res = await fetch(
    `/api/packaging-boms/${encodeURIComponent(bomId)}/lines/${encodeURIComponent(lineId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  )
  const body = await parseJson<
    { ok: true; line: PackagingBomLine } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Cập nhật dòng đóng gói thất bại')
  return (body as { line: PackagingBomLine }).line
}

export async function deletePackagingBomLineApi(
  bomId: string,
  lineId: string,
): Promise<void> {
  const res = await fetch(
    `/api/packaging-boms/${encodeURIComponent(bomId)}/lines/${encodeURIComponent(lineId)}`,
    {
      method: 'DELETE',
    },
  )
  const body = await parseJson<{ ok: true } | PackagingBomsApiError>(res)
  throwIfApiError(res, body, 'Xóa dòng đóng gói thất bại')
}

export async function saveActivePackagingBomApi(
  bomId: string,
  input: {
    name?: string
    notes?: string | null
    base_version_seq?: number
    lines?: Array<{
      id?: string
      name_raw: string
      spec_raw?: string | null
      material_id?: string | null
      qty_per: number
      uom_hint?: string | null
      notes?: string | null
      sort_order?: number
    }>
  },
): Promise<PackagingBomDetail> {
  const res = await fetch(
    `/api/packaging-boms/${encodeURIComponent(bomId)}/save-active`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  const body = await parseJson<
    { ok: true; packagingBom: PackagingBomDetail } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Lưu phiên bản Packaging BOM thất bại')
  return (body as { packagingBom: PackagingBomDetail }).packagingBom
}

export async function getPackagingBomFamilyVersionsApi(
  familyId: string,
): Promise<PackagingBom[]> {
  const res = await fetch(
    `/api/packaging-boms/families/${encodeURIComponent(familyId)}/versions`,
  )
  const body = await parseJson<
    { ok: true; family_id: string; versions: PackagingBom[] } | PackagingBomsApiError
  >(res)
  throwIfApiError(res, body, 'Tải lịch sử phiên bản Packaging BOM thất bại')
  return (body as { versions: PackagingBom[] }).versions
}

export async function comparePackagingBomsApi(
  baseId: string,
  targetId: string,
): Promise<any> {
  const res = await fetch(
    `/api/packaging-boms/compare?base_id=${encodeURIComponent(baseId)}&target_id=${encodeURIComponent(targetId)}`,
  )
  const body = await parseJson<any>(res)
  throwIfApiError(res, body, 'So sánh Packaging BOM thất bại')
  return body
}

export async function deletePackagingBomApi(id: string): Promise<void> {
  const res = await fetch(`/api/packaging-boms/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  const body = await parseJson<{ ok: true } | PackagingBomsApiError>(res)
  throwIfApiError(res, body, 'Xóa Packaging BOM thất bại')
}
